import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(undefined); // undefined = loading, null = anonymous, object = logged in
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const r = await api.get("/auth/me");
            setUser(r.data);
            return r.data;
        } catch {
            setUser(null);
            return null;
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(async (email, password) => {
        setError(null);
        try {
            const r = await api.post("/auth/login", { email, password });
            setUser({ ...r.data, active: true });
            await refresh();
            return true;
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            return false;
        }
    }, [refresh]);

    const register = useCallback(async (email, password, name) => {
        setError(null);
        try {
            const r = await api.post("/auth/register", { email, password, name });
            setUser({ ...r.data, active: true });
            await refresh();
            return true;
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            return false;
        }
    }, [refresh]);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout", {});
        } catch {
            /* ignore */
        }
        setUser(null);
    }, []);

    return (
        <AuthContext.Provider value={{ user, error, setError, login, register, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
    return ctx;
}
